/**
 * Pure interpretation functions for generating qualitative reports.
 * Takes scoring output from scoringEngine.js and returns interpretation objects.
 */

import {
  MSS_INTERPRETATIONS,
  DCIS_INTERPRETATIONS,
  TARGETS,
  DCI_ITEM_NUMBER_MAP,
  DCI_SUBSCALE_LEGEND,
} from './interpretationConfig.js'
import { DCI_ITEMS, DCI_TOTAL_ITEMS, IPIP_FACETS, DOMAIN_FACETS } from './scoringConfig.js'
import i18n from './i18n.js'
import { formConfig } from './formData.js'

// ─── Question Text Lookup ────────────────────────────────────────────────────

// Lazily build a flat map of questionId → question text from the DCI matrix groups.
// Resolved via i18n so text matches the active language.
let _dciQuestionTextMap = null
let _cachedLng = null

function getDciQuestionTextMap() {
  const lng = i18n.language
  if (_dciQuestionTextMap && _cachedLng === lng) return _dciQuestionTextMap
  _cachedLng = lng
  _dciQuestionTextMap = {}
  for (const group of formConfig.matrixGroups) {
    if (group.section === 'dyadic') {
      for (const q of group.questions) {
        _dciQuestionTextMap[q.id] = q.titleKey
          ? i18n.t(q.titleKey, { ns: 'survey' })
          : q.title
      }
    }
  }
  return _dciQuestionTextMap
}

// ─── MSS Interpretation ─────────────────────────────────────────────────────

/**
 * Interpret the Marital Satisfaction Score with narrative and gap analysis.
 * @param {number} msScore - The computed MS Score
 * @param {string} category - Category label from computeMSScore()
 * @returns {{ score: number, category: string, narrative: string, gap: number, gapText: string }}
 */
export function interpretMSS(msScore, category) {
  const interp = MSS_INTERPRETATIONS[category]
  const narrative = interp?.narrative || ''

  // Round score first, then compute gap from rounded value for display consistency
  const roundedScore = Math.round(msScore)
  const gap = TARGETS.MSS - roundedScore
  let gapText
  if (gap > 0) {
    gapText = `The client's MSS is ${gap} points below the target MSS score of ${TARGETS.MSS}.`
  } else if (gap < 0) {
    gapText = `The client's MSS is ${Math.abs(gap)} points above the target MSS score of ${TARGETS.MSS}.`
  } else {
    gapText = `The client has achieved the target MSS score of ${TARGETS.MSS}.`
  }

  return { score: roundedScore, category, narrative, gap, gapText }
}

// ─── DCIS Interpretation ────────────────────────────────────────────────────

/**
 * Interpret the Dyadic Coping Inventory Score with narrative and gap analysis.
 * @param {number} dciTotal - Total DCI score from scoreDCI()
 * @param {string} category - Category label from scoreDCI()
 * @returns {{ score: number, category: string, narrative: string, gap: number, gapText: string }}
 */
export function interpretDCIS(dciTotal, category) {
  const interp = DCIS_INTERPRETATIONS[category]
  const narrative = interp?.narrative || ''

  const gap = TARGETS.DCIS - dciTotal
  let gapText
  if (gap > 0) {
    gapText = `The client's DCIS is ${gap} points below the target DCIS score of ${TARGETS.DCIS}.`
  } else if (gap < 0) {
    gapText = `The client's DCIS is ${Math.abs(gap)} points above the target DCIS score of ${TARGETS.DCIS}.`
  } else {
    gapText = `The client has achieved the target DCIS score of ${TARGETS.DCIS}.`
  }

  return { score: dciTotal, category, narrative, gap, gapText }
}

// ─── Dyadic Coping Risk Factors ──────────────────────────────────────────────

/**
 * Identify DCI items that need improvement based on scored values.
 * Priority 1: items scored ≤ 2; Priority 2: items scored = 3.
 * Only examines items 1-35 (excludes EDC evaluation items).
 *
 * @param {Object<string, number>} itemScores - Post-reverse-scored item values from scoreDCI()
 * @returns {Array<{ priority: number, itemNo: number, questionId: string, questionText: string, subscale: string, subscaleLabel: string, currentScore: number, targetScore: number }>}
 */
export function identifyDyadicCopingRisks(itemScores) {
  const risks = []

  for (const itemId of DCI_TOTAL_ITEMS) {
    const score = itemScores[itemId]
    if (score == null) continue

    let priority = null
    if (score <= 2) priority = 1
    else if (score === 3) priority = 2

    if (priority) {
      const config = DCI_ITEMS[itemId]
      risks.push({
        priority,
        itemNo: DCI_ITEM_NUMBER_MAP[itemId] || 0,
        questionId: itemId,
        questionText: getDciQuestionTextMap()[itemId] || itemId,
        subscale: config.scale,
        subscaleLabel: DCI_SUBSCALE_LEGEND[config.scale] || config.scale,
        currentScore: score,
        targetScore: 5,
      })
    }
  }

  // Sort by priority (1 first), then by current score ascending (worst first)
  risks.sort((a, b) => a.priority - b.priority || a.currentScore - b.currentScore)

  return risks
}

/**
 * Calculate potential DCIS increase if all risk factor items reach their target.
 * @param {Array} risks - Output from identifyDyadicCopingRisks()
 * @param {number} currentDCIS - Current total DCI score
 * @returns {{ totalIncrease: number, projectedDCIS: number, wouldReachTarget: boolean }}
 */
export function calculateDCISPotential(risks, currentDCIS) {
  const totalIncrease = risks.reduce((sum, r) => sum + (r.targetScore - r.currentScore), 0)
  const projectedDCIS = currentDCIS + totalIncrease
  return {
    totalIncrease,
    projectedDCIS,
    wouldReachTarget: projectedDCIS >= TARGETS.DCIS,
  }
}

// ─── Personality Risk Factors ────────────────────────────────────────────────

/**
 * Identify personality traits that may contribute to dyadic stress.
 * Priority 1: N facets with converted score ≤ 2 (high neuroticism, reverse-coded)
 * Priority 2: C/A facets with converted score ≤ 2 (low conscientiousness/agreeableness)
 * Priority 3: E/O facets with |partner difference| ≥ 2
 *
 * @param {{ facets: Object }} person1IPIP - IPIP scores for person 1 (male)
 * @param {{ facets: Object }} person2IPIP - IPIP scores for person 2 (female)
 * @returns {{ person1Risks: Array, person2Risks: Array }}
 */
export function identifyPersonalityRisks(person1IPIP, person2IPIP) {
  const person1Risks = []
  const person2Risks = []

  // Priority 1: Neuroticism facets with converted ≤ 2 (high neuroticism)
  for (const facetCode of DOMAIN_FACETS.N) {
    const p1Conv = person1IPIP.facets[facetCode]?.converted
    const p2Conv = person2IPIP.facets[facetCode]?.converted
    const facetName = IPIP_FACETS[facetCode]

    const diff = (p1Conv != null && p2Conv != null) ? Math.abs(p1Conv - p2Conv) : null

    if (p1Conv != null && p1Conv <= 2) {
      person1Risks.push({
        priority: 1,
        facetRef: facetCode,
        facetName,
        hisScore: p1Conv,
        herScore: p2Conv,
        diff,
      })
    }
    if (p2Conv != null && p2Conv <= 2) {
      person2Risks.push({
        priority: 1,
        facetRef: facetCode,
        facetName,
        hisScore: p1Conv,
        herScore: p2Conv,
        diff,
      })
    }
  }

  // Priority 2: Conscientiousness and Agreeableness facets with converted ≤ 2
  for (const domain of ['C', 'A']) {
    for (const facetCode of DOMAIN_FACETS[domain]) {
      const p1Conv = person1IPIP.facets[facetCode]?.converted
      const p2Conv = person2IPIP.facets[facetCode]?.converted
      const facetName = IPIP_FACETS[facetCode]

      const diff = (p1Conv != null && p2Conv != null) ? Math.abs(p1Conv - p2Conv) : null

      if (p1Conv != null && p1Conv <= 2) {
        person1Risks.push({
          priority: 2,
          facetRef: facetCode,
          facetName,
          hisScore: p1Conv,
          herScore: p2Conv,
          diff,
        })
      }
      if (p2Conv != null && p2Conv <= 2) {
        person2Risks.push({
          priority: 2,
          facetRef: facetCode,
          facetName,
          hisScore: p1Conv,
          herScore: p2Conv,
          diff,
        })
      }
    }
  }

  // Priority 3: Extraversion and Openness facets with |difference| ≥ 2
  for (const domain of ['E', 'O']) {
    for (const facetCode of DOMAIN_FACETS[domain]) {
      const p1Conv = person1IPIP.facets[facetCode]?.converted
      const p2Conv = person2IPIP.facets[facetCode]?.converted
      if (p1Conv == null || p2Conv == null) continue

      const diff = Math.abs(p1Conv - p2Conv)
      if (diff >= 2) {
        const facetName = IPIP_FACETS[facetCode]
        const risk = {
          priority: 3,
          facetRef: facetCode,
          facetName,
          hisScore: p1Conv,
          herScore: p2Conv,
          diff,
        }
        // E/O mismatch is a shared couple issue — add to both
        person1Risks.push(risk)
        person2Risks.push(risk)
      }
    }
  }

  // Sort each by priority, then facetRef
  const sortFn = (a, b) => a.priority - b.priority || a.facetRef.localeCompare(b.facetRef)
  person1Risks.sort(sortFn)
  person2Risks.sort(sortFn)

  return { person1Risks, person2Risks }
}
